import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, TrendingUp, BookOpen, Car, GraduationCap, MapPin, RefreshCw } from "lucide-react";

function KpiCard({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-3 ${accent || "border-neutral-200 bg-white"}`}>
      <div className="text-[10px] uppercase font-semibold tracking-wide text-neutral-500">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-neutral-500">
      <Icon className="h-4 w-4 text-orange-600" /> {children}
    </h3>
  );
}

export default function StatisticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/statistics/overview");
      setData(d);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <div className="py-12 text-center text-sm text-neutral-500 flex items-center justify-center gap-2">
        <Loader2 className="h-5 w-5 animate-spin" /> Cargando estadísticas…
      </div>
    );
  }

  const c = data.comercial || {};
  const t = data.teorico || {};
  const p = data.practico || {};

  return (
    <div className="space-y-8" data-testid="statistics-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Estadísticas</h2>
          <p className="text-sm text-neutral-500 mt-1">
            Captación, conversión, exámenes y rendimiento — datos reales del sistema.
          </p>
        </div>
        <button type="button" onClick={load} className="btn-secondary !h-9 !text-xs" data-testid="stats-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Actualizar
        </button>
      </div>

      {/* Comerciales */}
      <section className="space-y-3">
        <SectionTitle icon={TrendingUp}>Estadísticas comerciales</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="stats-comercial">
          <KpiCard label="Prospectos" value={c.prospecto ?? 0} accent="border-blue-200 bg-blue-50" />
          <KpiCard label="Interesados" value={c.interesado ?? 0} accent="border-amber-200 bg-amber-50" />
          <KpiCard label="Alumnos activos" value={c.alumno_activo ?? 0} accent="border-emerald-200 bg-emerald-50" />
          <KpiCard label="Aptos" value={c.apto ?? 0} accent="border-emerald-300 bg-emerald-50" />
          <KpiCard label="No interesados" value={c.no_interesado ?? 0} accent="border-rose-200 bg-rose-50" />
          <KpiCard label="Bajas" value={c.baja ?? 0} accent="border-neutral-200 bg-neutral-50" />
          <KpiCard label="Traslados" value={c.traslado ?? 0} accent="border-sky-200 bg-sky-50" />
        </div>
      </section>

      {/* Teórico */}
      <section className="space-y-3">
        <SectionTitle icon={BookOpen}>Estadísticas teórico</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="stats-teorico">
          <KpiCard label="Presentados" value={t.presentados ?? 0} />
          <KpiCard label="Aptos" value={t.aptos ?? 0} accent="border-emerald-200 bg-emerald-50" />
          <KpiCard label="No aptos" value={t.no_aptos ?? 0} accent="border-rose-200 bg-rose-50" />
          <KpiCard label="% aprobación" value={`${t.pct_aprobacion ?? 0}%`} />
          <KpiCard label="Convocatoria media" value={t.media_convocatorias ?? 0} />
        </div>
      </section>

      {/* Práctico */}
      <section className="space-y-3">
        <SectionTitle icon={Car}>Estadísticas práctico</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="stats-practico">
          <KpiCard label="Presentados" value={p.presentados ?? 0} />
          <KpiCard label="Aptos" value={p.aptos ?? 0} accent="border-emerald-200 bg-emerald-50" />
          <KpiCard label="No aptos" value={p.no_aptos ?? 0} accent="border-rose-200 bg-rose-50" />
          <KpiCard label="% aprobación" value={`${p.pct_aprobacion ?? 0}%`} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <KpiCard label="A la primera" value={p.primera ?? 0} />
          <KpiCard label="A la segunda" value={p.segunda ?? 0} />
          <KpiCard label="A la tercera" value={p.tercera ?? 0} />
          <KpiCard label="Más de tercera" value={p.mas_tercera ?? 0} />
        </div>
      </section>

      {/* Profesor */}
      <section className="space-y-3">
        <SectionTitle icon={GraduationCap}>Estadísticas por profesor</SectionTitle>
        {(data.profesores || []).length === 0 ? (
          <div className="rounded-lg border border-dashed border-neutral-300 py-6 text-center text-sm text-neutral-500">
            Sin exámenes con profesor asignado todavía.
          </div>
        ) : (
          <div className="card-surface overflow-x-auto">
            <table className="w-full text-sm table-fixed" data-testid="stats-profesores-table">
              <colgroup>
                <col />
                <col style={{ width: "130px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "150px" }} />
                <col style={{ width: "150px" }} />
              </colgroup>
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500 text-left">
                <tr>
                  <th className="px-4 py-3">Profesor</th>
                  <th className="px-4 py-3 text-right">Presentados</th>
                  <th className="px-4 py-3 text-right">Aptos teórico</th>
                  <th className="px-4 py-3 text-right">Aptos práctico</th>
                  <th className="px-4 py-3 text-right">Ratio aprobación</th>
                  <th className="px-4 py-3 text-right">Ratio suspensos</th>
                </tr>
              </thead>
              <tbody>
                {data.profesores.map((pr) => (
                  <tr key={pr.teacher} className="border-t border-neutral-100 hover:bg-neutral-50/60 h-[56px]">
                    <td className="px-4 py-3 font-semibold align-middle">
                      <span className="block truncate" title={pr.teacher}>{pr.teacher}</span>
                    </td>
                    <td className="px-4 py-3 align-middle text-right tabular-nums">{pr.presentados}</td>
                    <td className="px-4 py-3 align-middle text-right tabular-nums">{pr.aptos_teorico}</td>
                    <td className="px-4 py-3 align-middle text-right tabular-nums">{pr.aptos_practico}</td>
                    <td className="px-4 py-3 align-middle text-right tabular-nums text-emerald-700 font-semibold">{pr.ratio_aprobacion}%</td>
                    <td className="px-4 py-3 align-middle text-right tabular-nums text-rose-700 font-semibold">{pr.ratio_suspensos}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Centro */}
      <section className="space-y-3">
        <SectionTitle icon={MapPin}>Estadísticas por centro de examen</SectionTitle>
        {(data.centros || []).length === 0 ? (
          <div className="rounded-lg border border-dashed border-neutral-300 py-6 text-center text-sm text-neutral-500">
            Sin exámenes registrados todavía.
          </div>
        ) : (
          <div className="card-surface overflow-x-auto">
            <table className="w-full text-sm table-fixed" data-testid="stats-centros-table">
              <colgroup>
                <col />
                <col style={{ width: "130px" }} />
                <col style={{ width: "110px" }} />
                <col style={{ width: "110px" }} />
                <col style={{ width: "150px" }} />
              </colgroup>
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500 text-left">
                <tr>
                  <th className="px-4 py-3">Centro</th>
                  <th className="px-4 py-3 text-right">Presentados</th>
                  <th className="px-4 py-3 text-right">Aptos</th>
                  <th className="px-4 py-3 text-right">No aptos</th>
                  <th className="px-4 py-3 text-right">% aprobación</th>
                </tr>
              </thead>
              <tbody>
                {data.centros.map((ce) => (
                  <tr key={ce.centro} className="border-t border-neutral-100 hover:bg-neutral-50/60 h-[56px]">
                    <td className="px-4 py-3 font-semibold align-middle">
                      <span className="block truncate" title={ce.centro}>{ce.centro}</span>
                    </td>
                    <td className="px-4 py-3 align-middle text-right tabular-nums">{ce.presentados}</td>
                    <td className="px-4 py-3 align-middle text-right tabular-nums text-emerald-700">{ce.aptos}</td>
                    <td className="px-4 py-3 align-middle text-right tabular-nums text-rose-700">{ce.no_aptos}</td>
                    <td className="px-4 py-3 align-middle text-right tabular-nums font-semibold">{ce.pct_aprobacion}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
